import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Button,
  Alert,
  AlertDescription,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Separator,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@roger/ui-components';
import {
  Search,
  Save,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import type { WorksheetGoal } from '../../types/worksheetGoals.types';
import {
  getWorksheetGoals,
  getWorksheetGoalsCount,
  getWorksheetGoalsCreationTypes,
} from '../../services/rogerDocApi';
import { useToast } from '../../hooks/useToastContext';
import GoalItem, { type GoalItemRef } from './GoalItem';
import { DISCIPLINE_OPTIONS } from '../../constants/worksheetGoals';

interface GoalsSectionProps {
  clinicKey: string | null;
  onClinicKeyChange: (clinicKey: string) => void;
  clinicKeyOptions: { value: string; label: string }[];
  showClinicSelector: boolean;
  agencyName?: string;
  onResync: () => void;
  isResyncing: boolean;
  isLoadingClinics: boolean;
  canResync: boolean;
  showResync: boolean;
  refetchTrigger?: number;
}

const GoalsSection: React.FC<GoalsSectionProps> = ({
  clinicKey,
  onClinicKeyChange,
  clinicKeyOptions,
  showClinicSelector,
  agencyName,
  onResync,
  isResyncing,
  isLoadingClinics,
  canResync,
  showResync,
  refetchTrigger = 0,
}) => {
  const [goals, setGoals] = useState<WorksheetGoal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(''); // Input value for immediate UI update
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering (only updated on Enter or Search button click)
  const [selectedDiscipline, setSelectedDiscipline] =
    useState('all_disciplines');
  const [showOnlyRequired, setShowOnlyRequired] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [goalsWithChanges, setGoalsWithChanges] = useState<Set<string>>(
    new Set()
  );
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [creationTypeByDiscipline, setCreationTypeByDiscipline] =
    useState<Record<string, string[]> | null>(null);

  // Pagination state
  const PAGE_SIZE = 100;
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const goalItemRefs = useRef<Record<string, GoalItemRef | null>>({});
  const { success, error: showError } = useToast();

  // Fetch creation types on component mount
  useEffect(() => {
    const fetchCreationTypes = async () => {
      try {
        const creationTypes = await getWorksheetGoalsCreationTypes();
        setCreationTypeByDiscipline(creationTypes);
      } catch (err) {
        console.error('Error fetching creation types:', err);
      }
    };

    fetchCreationTypes();
  }, []);

  // Handle search submission (triggered by Enter key or Search button)
  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setPage(1); // Reset page when search changes
  };

  // Handle Enter key press in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Callback for when a goal's change state updates
  const handleGoalChangeStateUpdate = (goalId: string, hasChanges: boolean) => {
    setGoalsWithChanges(prev => {
      const newSet = new Set(prev);
      if (hasChanges) {
        newSet.add(goalId);
      } else {
        newSet.delete(goalId);
      }
      return newSet;
    });
  };

  // Callback for when a goal is successfully saved
  const handleGoalUpdate = (updatedGoal: WorksheetGoal) => {
    setGoals(prevGoals =>
      prevGoals.map(goal =>
        goal._id === updatedGoal._id || goal.goal_key === updatedGoal.goal_key
          ? updatedGoal
          : goal
      )
    );
  };

  // Callback for when an individual goal is saved successfully
  const handleIndividualSaveSuccess = (goalName: string) => {
    success('Goal Saved', `${goalName}`);
  };

  // Check if any goals have changes
  const hasAnyChanges = goalsWithChanges.size > 0;

  // Handle individual goal expand/collapse
  const handleGoalToggleExpanded = (goalId: string) => {
    setExpandedGoals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(goalId)) {
        newSet.delete(goalId);
      } else {
        newSet.add(goalId);
      }
      return newSet;
    });
  };

  // Toggle all goals expand/collapse
  const handleToggleAll = () => {
    const allGoalIds = goals.map(goal => goal._id || goal.goal_key);
    const allExpanded = allGoalIds.every(id => expandedGoals.has(id));

    if (allExpanded) {
      setExpandedGoals(new Set());
    } else {
      setExpandedGoals(new Set(allGoalIds));
    }
  };

  // Save all goals with changes
  const handleSaveAll = async () => {
    const goalRefsWithChanges = Object.values(goalItemRefs.current).filter(
      ref => ref?.hasChanges
    );

    if (goalRefsWithChanges.length === 0) return;

    setIsSavingAll(true);

    const goalNamesToSave: string[] = [];
    Object.entries(goalItemRefs.current).forEach(([goalKey, ref]) => {
      if (ref?.hasChanges) {
        const goal = goals.find(g => (g._id || g.goal_key) === goalKey);
        if (goal) {
          goalNamesToSave.push(goal.goal_name || 'Unnamed Goal');
        }
      }
    });

    try {
      await Promise.all(goalRefsWithChanges.map(ref => ref!.save(true)));

      if (goalNamesToSave.length === 1) {
        success('Goal Saved', goalNamesToSave[0]);
      } else if (goalNamesToSave.length <= 3) {
        success('Goals Saved', goalNamesToSave.join(', '));
      } else {
        success(
          'Goals Saved',
          `${goalNamesToSave.length} goals saved: ${goalNamesToSave.slice(0, 2).join(', ')} and ${goalNamesToSave.length - 2} more`
        );
      }

      setGoalsWithChanges(new Set());
    } catch (err) {
      console.error('Error saving some goals:', err);
      showError('Save Failed', 'Failed to save some goals');
    } finally {
      setIsSavingAll(false);
    }
  };

  // Fetch goals when clinic_key changes or filters change
  useEffect(() => {
    const fetchGoals = async () => {
      if (!clinicKey) {
        setGoals([]);
        setTotalCount(0);
        setTotalPages(1);
        return;
      }

      setLoading(true);
      setError(null);
      setExpandedGoals(new Set()); // Collapse all when fetching new data

      try {
        const actualClinicKey = clinicKey === 'all_clinics' ? null : clinicKey;

        const [goalsData, count] = await Promise.all([
          getWorksheetGoals(actualClinicKey, {
            goal_search_string: searchTerm,
            discipline: selectedDiscipline,
            required: showOnlyRequired,
            page_size: PAGE_SIZE,
            page_number: page,
          }),
          getWorksheetGoalsCount(actualClinicKey, {
            goal_search_string: searchTerm,
            discipline: selectedDiscipline,
            required: showOnlyRequired,
          }),
        ]);

        setGoals(goalsData);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / PAGE_SIZE) || 1);
        success(
          'Goals Loaded',
          `${goalsData.length} goals loaded successfully`
        );
      } catch (err) {
        console.error('Error fetching goals:', err);
        setError('Failed to load goals. Please try again.');
        setGoals([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [
    clinicKey,
    refetchTrigger,
    searchTerm,
    selectedDiscipline,
    showOnlyRequired,
    page,
  ]);

  const allExpanded =
    goals.length > 0 &&
    goals.every(goal => expandedGoals.has(goal._id || goal.goal_key));

  // Get the clinic name for display (for the summary text)
  const clinicName = useMemo(() => {
    if (!clinicKey) return '';
    if (clinicKey === 'all_clinics') return 'All Clinics';

    if (!showClinicSelector && agencyName) {
      return agencyName;
    }

    const clinic = clinicKeyOptions.find(option => option.value === clinicKey);
    return clinic?.label || clinicKey;
  }, [clinicKey, clinicKeyOptions, showClinicSelector, agencyName]);

  // Helper function to get clinic name for a specific goal
  const getClinicNameForGoal = (goalClinicKey: string): string => {
    if (!showClinicSelector && agencyName) {
      return agencyName;
    }

    const clinic = clinicKeyOptions.find(
      option => option.value === goalClinicKey
    );
    return clinic?.label || goalClinicKey;
  };

  return (
    <>
      {/* Clinic Selector and Resync Controls. Hidden entirely for non-Kinnser
          agencies (e.g. TSC), which have no clinic selector and no Wellsky to
          resync from. */}
      {(showClinicSelector || showResync) && (
        <Card>
          <CardContent>
            <div className='flex gap-4 items-end flex-wrap'>
              {showClinicSelector && (
                <div className='flex flex-col gap-1 flex-1 min-w-[200px]'>
                  <label htmlFor='clinicKey' className='text-xs font-medium'>
                    Clinic
                  </label>
                  <Select
                    value={clinicKey || undefined}
                    onValueChange={onClinicKeyChange}
                  >
                    <SelectTrigger id='clinicKey'>
                      <SelectValue placeholder='Select clinic key' />
                    </SelectTrigger>
                    <SelectContent>
                      {clinicKeyOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {showResync && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={onResync}
                        disabled={isResyncing || !canResync}
                      >
                        {isResyncing ? (
                          <>
                            <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <RefreshCw className='h-4 w-4 mr-2' />
                            Resync from Wellsky
                          </>
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className='max-w-xs text-sm'>
                        Sync the information in the Roger system with what you
                        have in Wellsky
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Goals List */}
      <Card>
        <CardContent>
          {/* Search, Filter, and Save All Controls — show whenever a clinic is selected, including zero matching goals */}
          {!loading && !error && clinicKey && (
            <div className='flex justify-between items-center gap-4'>
              {/* Left side: Search and filter controls */}
              <div className='flex items-center gap-3 flex-1'>
                <div className='flex-1 max-w-md relative'>
                  <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                  <Input
                    placeholder='Search goals by name...'
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    className='pl-9 pr-24'
                  />
                  <Button
                    onClick={handleSearchSubmit}
                    className='absolute right-1 top-1/2 transform -translate-y-1/2'
                    size='sm'
                  >
                    Search
                  </Button>
                </div>
                <Select
                  value={selectedDiscipline}
                  onValueChange={val => {
                    setSelectedDiscipline(val);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className='w-48'>
                    <SelectValue placeholder='Filter by discipline' />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCIPLINE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='flex items-center space-x-2'>
                  <Checkbox
                    id='show-required'
                    checked={showOnlyRequired}
                    onCheckedChange={checked => {
                      setShowOnlyRequired(!!checked);
                      setPage(1);
                    }}
                  />
                  <label
                    htmlFor='show-required'
                    className='text-sm font-medium cursor-pointer whitespace-nowrap'
                  >
                    Show only required
                  </label>
                </div>
              </div>

              {/* Right side: Expand/Collapse and Save All */}
              <div className='flex items-center gap-2'>
                <Button
                  variant='outline'
                  onClick={handleToggleAll}
                  className='min-w-[120px]'
                >
                  {allExpanded ? (
                    <>
                      <ChevronRight className='h-4 w-4 mr-2' />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronDown className='h-4 w-4 mr-2' />
                      Expand All
                    </>
                  )}
                </Button>
                <Button
                  variant={hasAnyChanges ? 'default' : 'outline'}
                  disabled={!hasAnyChanges}
                  onClick={handleSaveAll}
                  className='min-w-[100px]'
                >
                  {isSavingAll ? (
                    <>
                      <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                      Saving
                    </>
                  ) : (
                    <>
                      <Save className='h-4 w-4 mr-2' />
                      Save All
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          <Separator className='my-8' />

          {/* Loading State */}
          {loading && (
            <div className='flex flex-col items-center justify-center py-12'>
              <Loader2 className='h-8 w-8 animate-spin text-gray-400 mb-4' />
              <p className='text-sm text-gray-600'>Loading goals...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <Alert className='border-red-200 bg-red-50'>
              <AlertTriangle className='h-4 w-4 text-red-600' />
              <AlertDescription className='text-red-800'>
                Error loading goals: {error}
              </AlertDescription>
            </Alert>
          )}

          {/* No Goals State */}
          {!loading && !error && goals.length === 0 && clinicKey && (
            <div className='flex flex-col items-center justify-center py-12'>
              <p className='text-sm text-gray-600 text-center'>
                {searchTerm.trim() ||
                selectedDiscipline !== 'all_disciplines' ||
                showOnlyRequired
                  ? 'No goals found matching your filters'
                  : `No goals found ${
                      clinicKey === 'all_clinics'
                        ? 'for any clinic'
                        : `for clinic: ${clinicName}`
                    }`}
              </p>
            </div>
          )}

          {/* Goals List */}
          {!loading && !error && goals.length > 0 && (
            <div className='space-y-4'>
              <p className='text-sm text-gray-600'>
                Found {totalCount} goal{totalCount !== 1 ? 's' : ''}
                {clinicKey === 'all_clinics'
                  ? ' across all clinics'
                  : ` for clinic: ${clinicName}`}
              </p>
              <div className='space-y-3'>
                {goals.map(goal => {
                  const goalId = goal._id || goal.goal_key;
                  // Filter used_by based on creation types for this goal's discipline
                  const disciplineName = goal.discipline?.discipline_name;
                  const allowedCreationTypes =
                    disciplineName && creationTypeByDiscipline
                      ? creationTypeByDiscipline[disciplineName] || []
                      : [];

                  return (
                    <GoalItem
                      key={goalId}
                      goal={goal}
                      clinicName={getClinicNameForGoal(goal.clinic_key)}
                      allowedCreationTypes={allowedCreationTypes}
                      onChangeStateUpdate={handleGoalChangeStateUpdate}
                      onGoalUpdate={handleGoalUpdate}
                      onSaveSuccess={handleIndividualSaveSuccess}
                      isExpanded={expandedGoals.has(goalId)}
                      onToggleExpanded={() => handleGoalToggleExpanded(goalId)}
                      ref={el => {
                        if (el) {
                          goalItemRefs.current[goalId] = el;
                        } else {
                          delete goalItemRefs.current[goalId];
                        }
                      }}
                    />
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className='flex justify-center mt-6 pb-4'>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          className={
                            page === 1
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <span className='flex h-9 items-center justify-center px-4 text-sm font-medium'>
                          Page {page} of {totalPages}
                        </span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            setPage(p => Math.min(totalPages, p + 1))
                          }
                          className={
                            page === totalPages
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </div>
          )}

          {/* No Clinic Selected */}
          {!clinicKey && (
            <div className='flex flex-col items-center justify-center py-12'>
              {isLoadingClinics ? (
                <>
                  <Loader2 className='h-8 w-8 animate-spin text-gray-400 mb-4' />
                  <p className='text-sm text-gray-600'>Loading clinics...</p>
                </>
              ) : (
                <p className='text-sm text-gray-600 text-center'>
                  Select a clinic key to view goals
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default GoalsSection;
